import styles from './main-menu.module.scss';
import React from 'react';
import { useLocation } from 'react-router-dom';
import { ReactNode, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { CheckboxLabel, Collapse, Icon, List, ListItemButton, ListItemIcon, ListItemText, ListItem, ListSubheader, Roles, checkMenuAccess } from '@frontend-workspace/react-shared-lib';
import { GetRoles } from '../TokenContext';

export interface MenuItemInterface {
  text: string,
  url?: string,
  isDisabled?: boolean,
  hiddenByDefault?: boolean,
  icon?: ReactNode,
  subCategories: MenuItemSubCategory[],
  defaultOpen?: boolean,
  id?: string,
  requiredRole?: Roles
}

export interface MenuItemSubCategory {
  text: string,
  url: string,
  isDisabled?: boolean,
  hiddenByDefault?: boolean,
  requiredRole?: Roles;
}

export interface MainMenuProps {
  menuItems: Array<MenuItemInterface>,
  currentCompanyNumber?: string,
  showHiddenItems?: boolean,
  onShowHiddenItemsChange?: (show: boolean) => void
}

export function MainMenu(props: MainMenuProps) {
  const { t } = useTranslation();
  const location = useLocation();
  const locationPathWithoutId = location.pathname.split("/");
  const pathWithoutCompanyNumber = props.currentCompanyNumber ? locationPathWithoutId.filter(path => path !== props.currentCompanyNumber).join("/") : location.pathname;
  const [open, setOpen] = useState<string[]>([]);
  const [expandedItems, setExpandedItems] = useState<{ [key: string]: boolean }>({});
  const userRole = GetRoles();

  const text = {
    fontFamily: 'Lato',
    fontWeight: 600
  };

  const handleToUrl = (url: string) => {
    if (props.currentCompanyNumber) {
      const urlParts = url.split("/");
      urlParts.splice(2, 0, props.currentCompanyNumber);
      return urlParts.join("/");
    }
    return url;
  }

  // Load saved menu state from localStorage
  useEffect(() => {
    const savedState = localStorage.getItem('menuState');
    if (savedState) {
      setExpandedItems(JSON.parse(savedState));
    }
  }, []);

  // Save menu state to localStorage whenever it changes
  useEffect(() => {
    localStorage.setItem('menuState', JSON.stringify(expandedItems));
  }, [expandedItems]);

  const handleToggleMenu = (id: string) => {
    setExpandedItems((prevState) => {
      const newState = { ...prevState, [id]: !prevState[id] };
      return newState;
    });
  };

  return <>
    <ListItem
      component="div"
      disablePadding
      sx={{ background: '#3D3B53' }}
      className="d-lg-none">
      <ListItemIcon className='me-2'>
        <img src="../assets/gfx_ico_Sogelink_max.png" alt="..." width="50px" height="50px" />
      </ListItemIcon>
      <ListItemText
        primary="KUA"
        primaryTypographyProps={{
          variant: 'h2Toolbar'
        }}
      />
    </ListItem>
    <List
      disablePadding
      subheader={
        <ListSubheader
          component='div'
          className='d-lg-none'
          sx={{ background: '#fff !important' }}>
          {t("mainMenu.navigation")}
        </ListSubheader>
      }>
      {props.menuItems.filter(i => props.showHiddenItems || !i.hiddenByDefault).map(i => (
        <React.Fragment key={i.id || i.text}>
          {checkMenuAccess(
            <ListItemButton onClick={() => handleToggleMenu(i.id || '')} sx={{ py: 0.5 }}>
              <ListItemIcon>
                <Icon>{i.icon}</Icon>
              </ListItemIcon>
              <ListItemText
                primary={i.text}
                primaryTypographyProps={{ style: text }}
              />
              {(open.includes(i.text)) ? <Icon>expand_less</Icon> : <Icon>expand_more</Icon>}
            </ListItemButton>,
            userRole,
            i.requiredRole
          )}
          {i.subCategories.filter(sub => props.showHiddenItems || !sub.hiddenByDefault).map(sub => (
            <React.Fragment key={sub.url}>
              {checkMenuAccess(
                <Collapse in={expandedItems[i.id || '']} timeout="auto">
                  <List component="div" disablePadding>
                    <ListItemButton
                      component={Link}
                      to={handleToUrl(sub.url)}
                      selected={sub.url === pathWithoutCompanyNumber}
                      sx={{ pl: 4, py: 0.5 }}>
                      <ListItemText primary={sub.text} />
                    </ListItemButton>
                  </List>
                </Collapse>,
                userRole,
                sub.requiredRole
              )}
            </React.Fragment>
          ))}
        </React.Fragment>
      ))}
      {props.onShowHiddenItemsChange && <ListItem>
        <CheckboxLabel label={t('mainMenu.showHiddenItems')} checked={props.showHiddenItems ?? false}
          onChange={(_, checked) => props.onShowHiddenItemsChange?.(checked)} />
      </ListItem>}
    </List>
  </>;
}

export default MainMenu;